import { Component, Input, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ClassReport } from '../data/class-report';
import { FormatUtilities } from '../utilities/format-utilities';
import { BaseComponent } from '../base/base.component';
import { Course } from '../data/course';
import { DateSparcScore } from '../data/speaking-score';
import { Chart } from 'chart.js';

@Component({
  selector: 'app-class-summary',
  templateUrl: './class-summary.component.html',
  styleUrls: ['./class-summary.component.scss']
})
export class ClassSummaryComponent extends BaseComponent implements OnInit {

  displaySkill = FormatUtilities.displaySkill;

  @Input() classID: number;
  @Input() organizationID: number;
  @Input() report: ClassReport;

  courses: Course[];

  currentClassAverageSparc: number;
  classAverageSparcHistory: DateSparcScore[];
  classAvgIncreased: boolean = false;
  classAvgDecreased: boolean = false;
  chart: Chart;
  chartInitialized: boolean = false;
  showSPARCData: boolean = true;
  @ViewChild('speakingScoreChart') chartElement!: ElementRef<HTMLCanvasElement>;

  ngOnInit() {
    this.dataService.getCourses().subscribe(c => this.courses = c);
    this.dataService.getClassSpeakingScoreHistory(this.classID).subscribe(s => this.getClassAverageScoreHistory(s));
  }

  ngAfterViewChecked() {
    this.showChart();
  }

  getCourseCodeAsLevel(courseCode: string): string {
    let course = this.courses.find(c => c.Code == courseCode);
    return FormatUtilities.getCourseNumberAsLevel(course?.NumberOnly);
  }

  getClassAverageScoreHistory(scores: DateSparcScore[]): void {
    this.classAverageSparcHistory = scores.filter(x => !Number.isNaN(x.AvgScore) && x.AvgScore !== undefined && x.AvgScore !== null);

    if (this.classAverageSparcHistory && this.classAverageSparcHistory.length > 0) {
      this.currentClassAverageSparc = this.classAverageSparcHistory[this.classAverageSparcHistory.length - 1].AvgScore;
      this.displaySpeakingScoreArrow();
      this.showChart();
    } else {
      this.showSPARCData = false;
    }
  }

  displaySpeakingScoreArrow(): void {
    if (this.currentClassAverageSparc && this.classAverageSparcHistory && this.classAverageSparcHistory.length > 1) {
      // the last element of the array is the current score, so we want to get the one before that
      var lastScore = this.classAverageSparcHistory[this.classAverageSparcHistory.length - 2]
      if (lastScore.AvgScore < this.currentClassAverageSparc) {
        this.classAvgIncreased = true;
      }
      else if (lastScore.AvgScore > this.currentClassAverageSparc) {
        this.classAvgDecreased = true;
      }
      // otherwise leave both as false and don't display an icon
    }
  }

  showChart() {
    if (this.chartInitialized || !this.chartElement || !this.classAverageSparcHistory || !this.classAverageSparcHistory.length || this.classAverageSparcHistory.length == 0) return;

    var dateArray: Array<Date> = this.classAverageSparcHistory.map(score => new Date(score.CalculationDate));

    // fill in the months that don't have data
    while (dateArray.length > 0 && dateArray.length < 12) {
      const lastDate = dateArray[dateArray.length - 1];
      const lastYear = lastDate.getFullYear();
      const lastMonth = lastDate.getMonth();
      // we can just use the first of the months that we don't have any data for
      const nextDate = lastMonth === 12 ? new Date(lastYear + 1, 1, 1) : new Date(lastYear, lastMonth + 1, 1);
      dateArray.push(nextDate);
    }

    var averageScoresArray: Array<number> = this.classAverageSparcHistory.map(score => score.AvgScore);

    const minAvgScore = Math.min.apply(Math, averageScoresArray);
    const maxAvgScore = Math.max.apply(Math, averageScoresArray);

    // adjust scale based on the difference between min and max scores
    const yAxisStep = (maxAvgScore - minAvgScore > 100) ? 100 : 50;

    const yAxisMin = Math.floor(minAvgScore / yAxisStep) * yAxisStep;
    const yAxisMax = Math.ceil(maxAvgScore / yAxisStep) * yAxisStep;

    this.chart = new Chart(this.chartElement.nativeElement, {
      type: 'line',
      data: {
        labels: dateArray.map(date => date.toLocaleDateString('default', { month: 'short' })),
        datasets: [
          {
            label: 'Score',
            data: averageScoresArray,
            borderColor: '#03B1F2',
            backgroundColor: '#03B1F2',
            borderWidth: 1,
            pointRadius: 2,
            order: 1
          }
        ]
      },
      options: {
        aspectRatio: 4.75,
        plugins: {
          title: {
            display: false
          },
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: 'white',
            borderColor: (tooltipItem) => {
              return '#03b1f2'
            },
            borderWidth: 1,
            caretPadding: 10,
            bodyAlign: 'center',
            bodyFont: {
              family: 'Lato',
              size: 16,
              weight: 600
            },
            bodyColor: '#212529',
            footerAlign: 'center',
            footerFont: {
              family: 'Lato',
              size: 12,
              weight: 200
            },
            footerColor: '#212529',
            footerMarginTop: 2,
            displayColors: false,
            padding: 6,
            callbacks: {
              title: function (_) { return ''; },
              label: function (context) {
                return context.formattedValue;
              },
              footer: function (_) { return ''; }
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                family: 'Lato',
                size: 10
              },
              minRotation: 0,
              maxRotation: 0
            }
          },
          y: {
            min: yAxisMin,
            max: yAxisMax,
            ticks: {
              font: {
                family: 'Lato',
                size: 10
              }
            }
          }
        },
      },
    });
    this.chartInitialized = true;
  }
}
